import type { IdPerfil } from './perfis';
import type { ACOES_DO_SPRINT, IdAcao, IdModulo } from './sprint';

/**
 * A MATRIZ DE PERMISSOES — E ELA E PROPOSTA, NAO DOCUMENTACAO.
 *
 * Isto nao e uma ressalva de rodape: e o assunto deste arquivo. O guia de
 * construcao descreve os oito perfis (secao 1.4) e pede RBAC com escopo por
 * empresa, filial, contrato e funcao (secao 14) — e NAO diz, em lugar nenhum,
 * o que cada perfil pode fazer. A matriz aparece tres vezes no guia como
 * pendencia, e no Apendice A ela esta como caixa nao marcada: "[ ] Matriz de
 * permissoes desenhada". O guia exige que ela seja desenhada antes do
 * desenvolvimento, e ela nao foi.
 *
 * Entao cada linha abaixo e uma DERIVACAO, e carrega a propria origem no campo
 * `porque`: a necessidade principal ou a decisao apoiada, da tabela 1.4, de
 * onde ela saiu. "Almoxarifado ve Combustivel" nao se discute; "necessidade:
 * saldo, reserva e demanda" se discute — e e para ser discutido que esta tela
 * existe. Ela nao e so login: e o instrumento para o dono desenhar a matriz
 * que o guia cobra.
 *
 * A EXCECAO E O ADMINISTRADOR, E ELA E DE OUTRA NATUREZA. Aquele perfil nao
 * esta na secao 1.4 (ver `PERFIS_DECIDIDOS_PELO_PROJETO`, em `perfis.ts`), e o
 * alcance dele nao foi derivado de necessidade nenhuma: foi decidido pelo dono
 * em 19/09/2026, com estas palavras — "tem que ter o usuario administrador onde
 * tem acesso a tudo". O `porque` de cada linha dele diz isso, em vez de citar
 * uma coluna que nao existe para ele. Continua valendo o resto: as acoes sao as
 * do Sprint 1 e nada foi inventado, e conceder acao desligada nao a liga.
 *
 * DUAS REGRAS QUE ESTE ARQUIVO SEGUE
 *
 * 1. Nenhuma acao inventada. `acoes` so aponta para ids de `ACOES_DO_SPRINT`,
 *    que e o inventario do que existe na tela hoje. Onde o perfil precisaria de
 *    algo que o Sprint 1 nao tem, o buraco esta escrito em `naoTemAcao` — em
 *    vez de virar uma permissao de mentira.
 *
 * 2. O que o perfil NAO alcanca nao mora aqui: ele e CALCULADO por diferenca em
 *    `consultas.ts`. Uma segunda lista escrita a mao envelheceria em silencio,
 *    e a metade que importa numa conversa sobre permissao e justamente essa.
 */

export interface ModuloProposto {
  readonly id: IdModulo;
  /**
   * De onde saiu, na tabela 1.4. Comeca por "necessidade:" ou "decisão
   * apoiada:" — os nomes das DUAS COLUNAS da secao 1.4, e nao um prefixo
   * qualquer. O prefixo e o que torna a linha conferivel contra o guia: quem
   * abre a tabela precisa achar a frase na coluna que o prefixo anuncia.
   *
   * Num perfil decidido pelo projeto nao ha coluna a citar, e o prefixo passa
   * a ser "decisão do dono", com a data. E o mesmo contrato: o prefixo anuncia
   * onde conferir, e conferir uma decisao e achar quem a tomou e quando.
   */
  readonly porque: string;
}

export interface AcaoProposta {
  readonly id: IdAcao;
  readonly porque: string;
}

export interface AlcanceProposto {
  readonly perfil: IdPerfil;
  readonly modulos: readonly ModuloProposto[];
  readonly acoes: readonly AcaoProposta[];
  /**
   * O que a necessidade do perfil pede e o Sprint 1 nao tem. Frase completa.
   * Vai para a tela como buraco reconhecido, nunca como permissao negada.
   */
  readonly naoTemAcao: readonly string[];
}

/**
 * A derivacao do administrador, uma so e repetida em cada linha dele — porque
 * e uma so de verdade. Variar a frase por modulo daria a impressao de que cada
 * item foi pensado contra alguma necessidade; nao foi: o alcance dele e "tudo",
 * e a razao de "Peças" estar la e exatamente a razao de "Custos" estar.
 */
const POR_DECISAO_DO_DONO = 'decisão do dono, 19/09/2026: acesso a tudo';

export const ALCANCE_PROPOSTO = [
  {
    perfil: 'diretoria',
    modulos: [
      { id: 'equipamentos', porque: 'necessidade: resultado consolidado e riscos' },
      { id: 'combustivel', porque: 'decisão apoiada: negociar' },
      { id: 'custos', porque: 'necessidade: resultado consolidado' },
      { id: 'relatorios', porque: 'necessidade: resultado consolidado e riscos' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'decisão apoiada: substituir e priorizar' },
      { id: 'frota.custo', porque: 'decisão apoiada: investir e negociar' },
      { id: 'frota.exportacao', porque: 'necessidade: resultado consolidado' },
      { id: 'combustivel.lista', porque: 'decisão apoiada: negociar' },
    ],
    naoTemAcao: [
      'Nenhuma escrita foi proposta. As quatro decisões deste perfil no guia são de decidir, e não de registrar operação.',
      'O resultado consolidado já tem o custo total por máquina — o direto, com a mão de obra pelo valor-hora da função, a depreciação e o custo indireto rateado —, em Custos e no painel; ele só se diz completo quando nada ficou fora do mês.',
    ],
  },
  {
    perfil: 'gestor-operacional',
    modulos: [
      { id: 'equipamentos', porque: 'necessidade: situação da frota e alocação' },
      { id: 'combustivel', porque: 'decisão apoiada: agir sobre ociosidade' },
      { id: 'manutencao', porque: 'necessidade: situação da frota' },
      { id: 'horimetro', porque: 'decisão apoiada: agir sobre ociosidade' },
      { id: 'obras', porque: 'necessidade: alocação' },
      { id: 'operadores', porque: 'necessidade: alocação' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'necessidade: situação da frota e alocação' },
      { id: 'frota.detalhe', porque: 'decisão apoiada: realocar' },
      { id: 'frota.custo', porque: 'decisão apoiada: agir sobre ociosidade' },
      { id: 'frota.exportacao', porque: 'necessidade: situação da frota' },
      { id: 'frota.cadastro', porque: 'decisão apoiada: mobilizar' },
      { id: 'combustivel.lista', porque: 'decisão apoiada: agir sobre ociosidade' },
    ],
    naoTemAcao: [
      'Mobilizar e realocar são a escrita central deste perfil, e ainda não têm ação própria nesta lista.',
    ],
  },
  {
    perfil: 'planejador-manutencao',
    modulos: [
      { id: 'equipamentos', porque: 'necessidade: vencimentos' },
      { id: 'manutencao', porque: 'decisão apoiada: programar parada' },
      { id: 'ordens', porque: 'decisão apoiada: programar parada' },
      { id: 'horimetro', porque: 'necessidade: vencimentos' },
      { id: 'pecas', porque: 'decisão apoiada: programar peças' },
      { id: 'operadores', porque: 'decisão apoiada: programar equipe' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'necessidade: vencimentos, que se contam pelo horímetro' },
      { id: 'frota.detalhe', porque: 'necessidade: vencimentos e recursos' },
    ],
    naoTemAcao: [
      'Programar parada, peças e equipe são as três decisões deste perfil. O plano preventivo, a fila de vencimentos e a O.S. preventiva aberta por ela já existem, e as peças também: a lista técnica da revisão aponta o que falta na tela de estoque. A equipe tem o cadastro de pessoas, com documentos e aptidões, mas ainda não tem escala para a parada.',
      'A coluna "Próx. manutenção" voltou à lista de equipamentos (decisão 16 do dono) e mostra a tarefa mais urgente de cada máquina; a fila completa, por tarefa, está na tela de manutenção.',
    ],
  },
  {
    perfil: 'oficina',
    modulos: [
      { id: 'equipamentos', porque: 'necessidade: diagnóstico' },
      { id: 'manutencao', porque: 'decisão apoiada: executar e liberar' },
      { id: 'ordens', porque: 'necessidade: OS e histórico' },
      { id: 'pecas', porque: 'decisão apoiada: executar' },
      { id: 'biblioteca', porque: 'necessidade: diagnóstico' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'necessidade: diagnóstico' },
      {
        id: 'frota.detalhe',
        porque: 'necessidade: histórico — é onde o motivo do status nomeia a O.S. aberta',
      },
    ],
    naoTemAcao: [
      'Executar, registrar e liberar acontecem na ordem de serviço, que já existe. A peça aplicada com depósito informado baixa o estoque, e a IA sugere uma hipótese de diagnóstico a partir do sintoma, citando o manual da máquina.',
      'Custo/hora ficou fora: a necessidade deste perfil é diagnóstico, OS e histórico, e não cita custo.',
    ],
  },
  {
    perfil: 'almoxarifado',
    modulos: [
      { id: 'pecas', porque: 'necessidade: saldo, reserva e demanda' },
      { id: 'ordens', porque: 'decisão apoiada: separar' },
      {
        id: 'combustivel',
        porque: 'necessidade: saldo — o saldo do tanque e o preço da última compra são deste perfil',
      },
    ],
    acoes: [
      { id: 'combustivel.lista', porque: 'decisão apoiada: rastrear' },
      { id: 'combustivel.lancamento', porque: 'decisão apoiada: repor e rastrear' },
    ],
    naoTemAcao: [
      'Saldo, reserva e a demanda futura das preventivas previstas já existem, na tela de estoque.',
      'A aplicação de lubrificante baixa o saldo do depósito quando o depósito é informado; sem ele, fica só o registro da aplicação.',
    ],
  },
  {
    perfil: 'compras',
    modulos: [
      { id: 'pecas', porque: 'necessidade: necessidade futura' },
      { id: 'combustivel', porque: 'decisão apoiada: cotar — o preço do litro por tanque' },
      { id: 'custos', porque: 'decisão apoiada: aprovar e comprar' },
      { id: 'relatorios', porque: 'necessidade: necessidade futura e lead time' },
    ],
    acoes: [{ id: 'combustivel.lista', porque: 'decisão apoiada: cotar' }],
    naoTemAcao: [
      'Aprovar e comprar já existem, na tela de compras: o pedido respeita a alçada cadastrada e pode voltar para ajuste. Cotar ainda não: não há comparação de propostas entre fornecedores.',
      'O prazo de entrega é registrado em cada pedido, e a necessidade de compra mostra o menor prazo dos pedidos em aberto; ainda não há uma média de quanto cada fornecedor costuma demorar.',
    ],
  },
  {
    perfil: 'controladoria',
    modulos: [
      { id: 'equipamentos', porque: 'necessidade: custos' },
      { id: 'combustivel', porque: 'necessidade: custos' },
      { id: 'custos', porque: 'necessidade: custos, receitas e margens' },
      { id: 'relatorios', porque: 'decisão apoiada: analisar desvios e rentabilidade' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'decisão apoiada: analisar desvios' },
      { id: 'frota.custo', porque: 'necessidade: custos e margens' },
      { id: 'frota.exportacao', porque: 'decisão apoiada: analisar desvios' },
      { id: 'combustivel.lista', porque: 'necessidade: custos' },
    ],
    naoTemAcao: [
      'Nenhuma escrita foi proposta: analisar não é registrar.',
      'A receita já existe: contratos, medições aprovadas e documentos de cobrança, com a margem por contrato sobre o custo total — direto, depreciação e custo indireto rateado, com a máquina de vários contratos dividida pela hora. A margem só se chama margem quando nada ficou fora do mês; senão é parcial, e diz por quê.',
    ],
  },
  {
    perfil: 'operador',
    modulos: [
      {
        id: 'equipamentos',
        porque: 'necessidade: apontamento simples — a máquina que ele opera',
      },
      { id: 'combustivel', porque: 'necessidade: apontamento simples' },
      { id: 'horimetro', porque: 'necessidade: apontamento simples' },
    ],
    acoes: [
      { id: 'frota.lista', porque: 'decisão apoiada: operar com segurança' },
      { id: 'combustivel.lancamento', porque: 'necessidade: apontamento simples' },
    ],
    naoTemAcao: [
      'A proposta é que o operador veja só a própria máquina, e o sistema ainda não faz isso: a lista de equipamentos não filtra por operador.',
      'Checklist é a necessidade deste perfil e registrar ocorrência é decisão apoiada dele. Os dois ficaram fora desta proposta, mas existem desde 01/10: o operador faz a inspeção, e o item não conforme abre a ocorrência sozinho.',
      'Custo/hora ficou fora: custo não aparece na necessidade nem nas decisões deste perfil.',
      'Sem celular, o caminho deste perfil é a folha de campo impressa: ele a preenche no papel, e quem escaneia confere o que a IA leu e lança. Com celular, o app (o endereço do sistema com /m) lança o abastecimento, a leitura do horímetro e o checklist pré-uso, mesmo sem sinal.',
    ],
  },
  /* O ADMINISTRADOR FICA POR ULTIMO, e depois de uma linha em branco, pela
     mesma razao que ele mora em outra lista em `perfis.ts`: ele nao e o nono
     item de uma tabela de oito. Todo modulo do mapa e toda acao do Sprint 1
     entram aqui — e e por isso que a coluna "O que nao alcanca" da tela fica
     vazia so para ele, dizendo, com todas as letras, que nao sobrou nada. */
  {
    perfil: 'administrador',
    modulos: [
      { id: 'equipamentos', porque: POR_DECISAO_DO_DONO },
      { id: 'combustivel', porque: POR_DECISAO_DO_DONO },
      { id: 'manutencao', porque: POR_DECISAO_DO_DONO },
      { id: 'ordens', porque: POR_DECISAO_DO_DONO },
      { id: 'horimetro', porque: POR_DECISAO_DO_DONO },
      { id: 'obras', porque: POR_DECISAO_DO_DONO },
      { id: 'operadores', porque: POR_DECISAO_DO_DONO },
      { id: 'pecas', porque: POR_DECISAO_DO_DONO },
      { id: 'custos', porque: POR_DECISAO_DO_DONO },
      { id: 'relatorios', porque: POR_DECISAO_DO_DONO },
      { id: 'biblioteca', porque: POR_DECISAO_DO_DONO },
    ],
    acoes: [
      { id: 'frota.lista', porque: POR_DECISAO_DO_DONO },
      { id: 'frota.detalhe', porque: POR_DECISAO_DO_DONO },
      { id: 'frota.custo', porque: POR_DECISAO_DO_DONO },
      { id: 'frota.exportacao', porque: POR_DECISAO_DO_DONO },
      { id: 'frota.cadastro', porque: POR_DECISAO_DO_DONO },
      { id: 'frota.exclusao', porque: POR_DECISAO_DO_DONO },
      { id: 'combustivel.lista', porque: POR_DECISAO_DO_DONO },
      { id: 'combustivel.lancamento', porque: POR_DECISAO_DO_DONO },
      { id: 'cadastros.estrutura', porque: POR_DECISAO_DO_DONO },
      { id: 'cadastros.governanca', porque: POR_DECISAO_DO_DONO },
      { id: 'cadastros.trilha', porque: POR_DECISAO_DO_DONO },
    ],
    naoTemAcao: [
      'As ações marcadas "Desligada" acima continuam desligadas para ele: conceder uma ação não a constrói.',
      'O módulo que ainda não existe aparece na lista acima com o marcador "Não construído". Ver tudo, hoje, é ver o que o sistema tem.',
      'Este perfil não tem necessidade própria no guia para comparar: o que falta para os outros oito falta para ele também. Papel e permissão se cadastram na governança, e o servidor confere a permissão em cada pedido.',
    ],
  },
] as const satisfies readonly AlcanceProposto[];

/**
 * VERIFICADOR DE COMPILACAO: TODO perfil do seletor tem proposta.
 *
 * `IdPerfil` e a uniao das DUAS listas de `perfis.ts` — os oito da secao 1.4 e
 * os decididos pelo projeto —, entao esta asercao cobre o administrador sem
 * precisar de linha nova: foi conferido removendo a proposta dele, e o `tsc`
 * reprova este arquivo. Sem ela, acrescentar um perfil (na tabela do guia ou na
 * lista de decisoes) e esquecer da matriz so apareceria no dia em que alguem
 * escolhesse aquele perfil no seletor — e apareceria como tela quebrada, nao
 * como lembrete. O `as const` acima existe
 * para esta conta: sem ele o tipo do array seria `AlcanceProposto[]` e a uniao
 * abaixo daria `IdPerfil` sempre, verificando nada.
 *
 * Se algum perfil ficar sem proposta, `Falta` deixa de ser `never`, o
 * condicional devolve `false` e `Afirmar` — cuja restricao e `extends true` —
 * para de compilar. A restricao e o que faz a verificacao existir: um apelido
 * de tipo que resolve para `never` nao e erro nenhum, e passaria calado.
 */
type Afirmar<T extends true> = T;
type PerfisComProposta = (typeof ALCANCE_PROPOSTO)[number]['perfil'];
type Falta = Exclude<IdPerfil, PerfisComProposta>;
export type TodosOsPerfisTemProposta = Afirmar<[Falta] extends [never] ? true : false>;

/**
 * VERIFICADOR DE COMPILACAO: toda acao concedida mora num modulo que o perfil
 * VE.
 *
 * A tela afirma as duas coisas lado a lado — "Modulos que este perfil ve" e "O
 * que pode ler / escrever" — e nada impedia conceder `frota.custo` a um perfil
 * sem `equipamentos` na lista de modulos. A tela entao prometeria "pode ler Ver
 * o custo por hora" e, quatro secoes abaixo, "Equipamentos — fora do perfil
 * nesta proposta", enquanto a navegacao esconderia o modulo inteiro. Hoje todas
 * as propostas estao coerentes — a do administrador por construcao, ja que ele
 * ve todo modulo do mapa: isto e o verificador que a regra do projeto cobra
 * ("toda invariante nova ganha verificador"), e nao o conserto de um defeito
 * ativo.
 *
 * `ModuloDaAcao` le o modulo de cada acao em `ACOES_DO_SPRINT` — por isso
 * aquela lista virou `as const satisfies`. Se sobrar um modulo depois do
 * `Exclude`, `AcaoForaDoModuloDoPerfil` deixa de ser `never`, `Afirmar` perde
 * a restricao `extends true` e o arquivo para de compilar.
 *
 * E UM TIPO MAPEADO, E NAO UMA CONDICIONAL DISTRIBUTIVA sobre a uniao das
 * entradas. Na condicional, o `E['acoes'][number]['id']` do ramo verdadeiro
 * resolve pela RESTRICAO do parametro e nao pela entrada instanciada: a conta
 * saia como se todo perfil concedesse todas as acoes do Sprint 1, e quatro dos
 * oito perfis — que estao coerentes — reprovavam. Com a chave `P` concreta em
 * cada posicao do mapeamento, cada perfil e avaliado sozinho. Foi conferido
 * nos dois sentidos: passa com a proposta de hoje, e reprova quando se concede
 * `frota.custo` a um perfil sem `equipamentos` na lista de modulos.
 */
type ModuloDaAcao<A extends IdAcao> = Extract<
  (typeof ACOES_DO_SPRINT)[number],
  { readonly id: A }
>['modulo'];

type EntradaDaProposta<P extends PerfisComProposta> = Extract<
  (typeof ALCANCE_PROPOSTO)[number],
  { readonly perfil: P }
>;

type AcaoForaDoModuloDoPerfil = {
  readonly [P in PerfisComProposta]: Exclude<
    ModuloDaAcao<EntradaDaProposta<P>['acoes'][number]['id']>,
    EntradaDaProposta<P>['modulos'][number]['id']
  >;
}[PerfisComProposta];

export type TodaAcaoEstaEmModuloQueOPerfilVe = Afirmar<
  [AcaoForaDoModuloDoPerfil] extends [never] ? true : false
>;

export function alcancePropostoDe(perfil: IdPerfil): AlcanceProposto {
  const achado = ALCANCE_PROPOSTO.find((alcance) => alcance.perfil === perfil);
  if (!achado) {
    throw new Error(`O perfil "${perfil}" não tem alcance proposto.`);
  }
  return achado;
}
